<template>
    <div class="d-flex flex-column">
        <navigator :selectedProduct="getSelectedItem">
            <template slot="actionButtons">
            <button v-if="currentStep > 1" type="button" class="btn blue wider mr-3" :disabled="!stepNavigation.canGoBack" @click="navigateStep(-1)">
                <i class="ico-arrow-left fs-normal ml-2"></i> Back
            </button>
            <button type="button" class="btn cancel wider mr-3" @click="cancel">Cancel</button>
            <button
                v-if="getSelectedItem || currentStep === 5"
                type="button"
                class="btn green wider mr-3"
                :disabled="!stepNavigation.canSave || hasAnyStepError"
                @click="handleSave"
            >Save</button>
            <button
                v-if="currentStep < 5"
                type="button"
                class="btn blue wider"
                :disabled="!stepNavigation.canProceed"
                @click="navigateStep(1)"
            >Next <i class="ico-arrow-right fs-normal ml-2"></i></button>
            </template>
        </navigator>

        <steps :currentStep="currentStep" :stepErrors="stepErrors" @switchStep="handleStepClick"></steps>

        <div class="flexGrow1" style="overflow: auto;">
            <component
                :is="stepTemplate"
                ref="activeStep"
                :currentStep="currentStep"
                @stepNavigationChanged="onStepNavigationChanged"
                @cancelling="cancel"
                @goToStep="goToStep"
                @readyToSave="save"
                @updateBackupGroup="updateBackupGroup">
            </component>
        </div>
    </div>
</template>

<script>
    import { mapActions, mapGetters } from 'vuex'
    import { productMixin } from '@/mixins/product'
    import productService from '@/api/product/productService'
    
    import Vue from 'vue';
    import ProductLoading from './ProductLoading'
    import ProductEdit from './ProductEdit'
    import ProductSchedule from './ProductSchedule'
    import ProductExtras from './ProductExtras'
    import ProductChannels from './ProductChannels'
    import ProductPickups from './ProductPickups'
    import Navigator from './ProductNavigator'
    import Steps from './ProductSteps'

    export default {
        mixins: [
            productMixin
        ],
        provide() {
            return {
                getProductBackup: () => this.productBackup
            };
        },
        data() {
            return {
                stepTemplate: 'ProductLoading',
                currentStep: 1,
                productBackup: null,
                stepNavigation: { canProceed: true, canSave: false, canGoBack: true },
                lastStepValidity: { canProceed: true, canSave: true, canGoBack: true },
                stepErrors: {},
                stepDirty: {},
                hasChanges: false,
                isProductLoaded: false,
            }
        },
        watch: {
            getClonedItem: {
                deep: true,
                handler() {
                    if (!this.isProductLoaded || this.hasChanges) return;
                    this.hasChanges = true;
                    this.stepNavigation = { ...this.lastStepValidity };
                }
            },
        },
        created() {
            if (this.getSelectedItem) {
                // If user comes from inventory
                this.downloadProduct(this.getSelectedItem.id);
                this.requestProductSchedulesRestrictions(this.getSelectedItem.id);
            } else if(this.$route.params.id){
                this.downloadProduct(this.$route.params.id);
                this.requestProductSchedulesRestrictions(this.$route.params.id);
            } else {
                this.stepTemplate = 'ProductEdit';
            }
        },
        beforeDestroy() {
            this.resetProductSchedulesRestrictions();
        },
        computed: {
            ...mapGetters([
                'getSelectedItem',
                'getClonedItem',
                'account',
                'getAvailabilityTypes'
            ]),
            isDirty() {
                if (!this.getSelectedItem) return true;
                return this.hasChanges || Object.values(this.stepDirty).some(Boolean);
            },
            hasAnyStepError() {
                return Object.values(this.stepErrors).some(Boolean);
            }
        },
        methods: {
            ...mapActions([
                'clearSelectedItem',
                'updateClonedItem',
                'addItem',
                'updateItem',
                'flushEvents',
                'setSelectedProduct',
                'openModal',
                'requestProductSchedulesRestrictions',
                'resetProductSchedulesRestrictions',
                'editProduct'
            ]),
            async downloadProduct(productId) {
                const result = await productService.getProductById(this.account.supplierId, productId);
                if (!result.ok) return;
                this.productBackup = structuredClone(result.value);
                this.updateClonedItem(result.value);
                this.setSelectedProduct(result.value);
                this.stepTemplate = 'ProductEdit';
                this.$nextTick(() => { this.isProductLoaded = true; });
                this.$forceUpdate();
            },
            updateBackupGroup(downloadedGroups) {
                this.$set(this.productBackup, 'groups', this.productBackup.groups.concat(downloadedGroups));
            },
            changeCurrentStep(step) {
                const stepTemplates = {
                    1: 'ProductEdit',
                    2: 'ProductSchedule',
                    3: 'ProductExtras',
                    4: 'ProductChannels',
                    5: 'ProductPickups',
                };

                this.stepTemplate = stepTemplates[step];
            },
            cancel() {
                this.clearSelectedItem();

                this.$router.push({ name: 'inventory' });
            },
            onStepNavigationChanged(validity) {
                this.lastStepValidity = validity;

                this.$set(this.stepErrors, this.currentStep, !!validity.hasError);
                
                this.$set(this.stepDirty, this.currentStep, !!validity.isDirty);

                this.stepNavigation = {
                    ...validity,
                    canSave: validity.canSave && this.isDirty,
                };
            },
            handleStepClick(targetStep) {
                if (targetStep === this.currentStep) return;
                const data = this.$refs.activeStep.getStepState();
                this.goToStep({ ...data, step: targetStep });
            },
            handleSave() {
                this.$refs.activeStep.readyToSave();
            },
            navigateStep(direction) {
                const data = this.$refs.activeStep.getStepState();
                this.goToStep({ ...data, step: this.currentStep + direction });
            },
            goToStep(data) {
                if (!data.$invalid) {
                    this.isProductLoaded = false;
                    this.stepNavigation = { canProceed: true, canSave: false, canGoBack: true };
                    this.currentStep = data.step;
                    this.updateClonedItem(data.form);
                    this.changeCurrentStep(this.currentStep);
                    this.$nextTick(() => { this.isProductLoaded = true; });
                }
            },
            save(data) {
                let vm = this;

                if (!data.invalid) {
                    this.updateClonedItem(data.form);

                    // Save resources inside each schedule
                    data.form.groups.map(group => {
                        group.components = data.form.extras;

                        // Get group dates inside each child schedule
                        group.schedules.map(schedule => {
                            schedule.dateFrom = group.dateFrom;
                            schedule.dateTo = group.dateTo;
                            schedule.duration = group.duration;
                            schedule.isTaxIncludedInPrice = group.tax;
                        });

                        group.productGenderPrices.map(productGenderPrice => {
                            productGenderPrice.isTaxIncludedInPrice = group.productGenderPrices[0].isTaxIncludedInPrice;
                        });
                    });

                    // Check other product changes to show on the "changes" resume popup
                    this.checkProductChanges(data, data.olderScheduleIds || []);

                    // Ensure the modal reads a fully up-to-date product snapshot (including 'changes')
                    const shouldOpenApplyAllEventsModal = (data.form && data.form.showApplyAllEventQuestion === true) || (this.getClonedItem && this.getClonedItem.showApplyAllEventQuestion === true);
                    if (shouldOpenApplyAllEventsModal) {
                        // Preserve the flag on the form before syncing to store to avoid accidental reset
                        this.$set(data.form, 'showApplyAllEventQuestion', true);
                    }
                    this.updateClonedItem(data.form);

                    if (this.getSelectedItem) {
                        if (shouldOpenApplyAllEventsModal) {
                            this.openModal('ProductSaveChanges');
                        } else {
                            // Save product
                            this.updateProduct(this.getClonedItem);
                        }
                    } else {
                        Vue.toasted.info('Saving product');
                        
                        this.addItem({
                            object: 'product',
                            action: 'create',
                            table: 'products',
                            fields: { supplierId: this.account.supplierId, product: data.form }
                        }).then((response) => {
                            if (response && response.status == 200) {
                                this.closeProduct();
                            }
                        }).catch((response) => {});
                    }
                }
            },
            checkProductChanges(input, olderScheduleIds = []) {
                if (!this.getClonedItem.id) {
                    return false;
                }

                let editedProduct = input.form;

                // Initialize changes object
                this.$set(editedProduct, 'changes', {
                    details:[],
                    salesChannels:[],
                    addedGroups:[],
                    removedGroups:[],
                    agentSelected: [],
                    salesPermissionsAgentsList: []
                })

                // Booking Question - If any of the booking question parameters is changed than
                // we show all the booking question setup
                if ((this.productBackup.bookingQuestion != editedProduct.bookingQuestion) ||
                    (this.productBackup.bookingQuestionIsActive != editedProduct.bookingQuestionIsActive) ||
                    (this.productBackup.bookingQuestionIsMandatory != editedProduct.bookingQuestionIsMandatory)) {
                    // activate popup
                    this.getClonedItem.showApplyAllEventQuestion = true;

                    // Question
                    this.pushChangeToArray(
                        editedProduct.changes.details,
                        'Booking Question',
                        'bookingQuestion',
                        null,
                        editedProduct.bookingQuestion,
                        null,
                        editedProduct.bookingQuestion,
                    );

                    // Active
                    this.pushChangeToArray(
                        editedProduct.changes.details,
                        'Booking question is Active',
                        'bookingQuestionIsActive',
                        null,
                        editedProduct.bookingQuestionIsActive == true? 'Yes':'No',
                        null,
                        editedProduct.bookingQuestionIsActive,
                    );

                    // Mandatory
                    this.pushChangeToArray(
                        editedProduct.changes.details,
                        'Booking question is Mandatory',
                        'bookingQuestionIsMandatory',
                        null,
                        editedProduct.bookingQuestionIsMandatory == true? 'Yes':'No',
                        null,
                        editedProduct.bookingQuestionIsMandatory,
                    );
                }

                // Location
                if (this.productBackup.location != editedProduct.location) {
                    // Activate popup
                    this.getClonedItem.showApplyAllEventQuestion = true
                    this.pushChangeToArray(
                        editedProduct.changes.details,
                        'Location',
                        'location',
                        this.productBackup.location,
                        editedProduct.location,
                        this.productBackup.location,
                        editedProduct.location,
                    );
                }

                // Schedule - Availability Type
                if (this.productBackup.availabilityType != editedProduct.availabilityType) {
                    // Activate popup
                    this.getClonedItem.showApplyAllEventQuestion = true;
                    let newValNiceName = this.getAvailabilityTypes.find(type => {return type.value == this.productBackup.availabilityType}).label;
                    let oldValNiceName = this.getAvailabilityTypes.find(type => {return type.value == editedProduct.availabilityType}).label;
                    this.pushChangeToArray(
                        editedProduct.changes.details,
                        'Availability Type',
                        'availabilityType',
                        newValNiceName,
                        oldValNiceName,
                        this.productBackup.availabilityType,
                        editedProduct.availabilityType,
                    );
                }

                // Schedule - Capacity (if "Fixed Limit")
                if (editedProduct.availabilityType == 0 && this.productBackup.capacity != editedProduct.capacity) {
                    // Activate popup
                    this.getClonedItem.showApplyAllEventQuestion = true;
                    this.pushChangeToArray(
                        editedProduct.changes.details,
                        'Capacity',
                        'capacity',
                        this.productBackup.capacity,
                        editedProduct.capacity,
                        this.productBackup.capacity,
                        editedProduct.capacity,
                    );
                }

                // Schedule - Virtual Roster
                if (this.productBackup.productShareType != editedProduct.productShareType) {
                    // Activate popup
                    this.getClonedItem.showApplyAllEventQuestion = true;
                    this.pushChangeToArray(
                        editedProduct.changes.details,
                        'Virtual Roster',
                        'productShareType',
                        this.productBackup.productShareType == 2?'Yes':'No',
                        editedProduct.productShareType == 2?'Yes':'No',
                        this.productBackup.productShareType,
                        editedProduct.productShareType,
                    );
                }

                // Agent selection / All agent changes
                if (this.productBackup.salesPermissionsAgentsList.length != editedProduct.salesPermissionsAgentsList.length) {
                    this.getClonedItem.showApplyAllEventQuestion = true;
                    this.pushChangeToArray(
                        editedProduct.changes.agentSelected,
                        'Agents',
                        'Agents',
                        this.productBackup.salesPermissionsAgentsList.length == 0 ? 'All agents' : 'Agent selection',
                        editedProduct.salesPermissionsAgentsList.length > 0 ? 'Agent selection' : 'All agents',
                        this.productBackup.salesPermissionsAgentsList.length == 0 ? false : true,
                        editedProduct.salesPermissionsAgentsList.length > 0 ? true : false
                    );

                    // Create an array of all agents selected
                    for (let salesPermissionsAgent of this.getClonedItem.salesPermissionsAgentsList) {
                        editedProduct.changes.salesPermissionsAgentsList.push({
                            newNiceValue: salesPermissionsAgent.brandName
                        });
                    }
                }

                // Sales Channels
                for (let bkpSalesChannel of this.productBackup.channelCommissions) {
                    let actualSalesChannel = editedProduct.channelCommissions.find(ss => {return ss.channelId == bkpSalesChannel.channelId});
                    
                    // Check if active status changed
                    if (bkpSalesChannel.isActive != actualSalesChannel.isActive) {
                        // Activate popup
                        this.getClonedItem.showApplyAllEventQuestion = true;
                        
                        this.pushChangeToArray(
                            editedProduct.changes.salesChannels,
                            actualSalesChannel.channelName,
                            'salesChannel'+actualSalesChannel.channelId,
                            bkpSalesChannel.isActive == true?'Open':'Closed',
                            actualSalesChannel.isActive == true?'Open':'Closed',
                            bkpSalesChannel.isActive,
                            actualSalesChannel.isActive,
                        );
                    }

                    // PRODUCT AND EXTRAS COMMISSION - check if value or value type changed
                    let commisionTypes = [
                        {
                            objPrefix:'product',
                            niceName:'Product',
                        },
                        {
                            objPrefix:'component',
                            niceName:'Extra',
                        },
                        {
                            objPrefix:'transport',
                            niceName:'Transport',
                        }];

                    // Iterate Product, Extras, Transport
                    for (var index of commisionTypes) {
                        if ((bkpSalesChannel[index.objPrefix+'AmountValue'] != actualSalesChannel[index.objPrefix+'AmountValue']) || bkpSalesChannel[index.objPrefix+'AmountType'] != actualSalesChannel[index.objPrefix+'AmountType']) {
                            // Activate popup
                            this.getClonedItem.showApplyAllEventQuestion = true;
                            
                            this.pushChangeToArray(
                                editedProduct.changes.salesChannels,
                                actualSalesChannel.channelName + ' ' + index.niceName + ' Commision',
                                'salesChannel'+actualSalesChannel.channelId+'Commision'+index.niceName,
                                `${bkpSalesChannel[index.objPrefix+'AmountValue']}${bkpSalesChannel[index.objPrefix+'AmountType'] == 1?'%':'€'}`,
                                `${actualSalesChannel[index.objPrefix+'AmountValue']}${actualSalesChannel[index.objPrefix+'AmountType'] == 1?'%':'€'}`,
                                `${bkpSalesChannel[index.objPrefix+'AmountValue']}${bkpSalesChannel[index.objPrefix+'AmountType'] == 1?'%':'€'}`,
                                `${actualSalesChannel[index.objPrefix+'AmountValue']}${actualSalesChannel[index.objPrefix+'AmountType'] == 1?'%':'€'}`,
                            );
                        }
                    }
                }

                // Removed groups
                for (let bkpGroup of this.productBackup.groups) {
                    if (olderScheduleIds.includes(bkpGroup.id)) continue;
                    // Check if bkpGroup still exists on editedProduct
                    let stillExists = editedProduct.groups.find(editedGroup => {return editedGroup.id == bkpGroup.id});
    
                    if (typeof stillExists != "object") {
                        this.pushChangeToArray(
                            editedProduct.changes.removedGroups,
                            'Removed Group',
                            'removedGroup',
                            bkpGroup.label,
                            null,
                            bkpGroup,
                            null,
                            true
                        );
                    }
                }

            },
            updateProduct(form) {
                Vue.toasted.info('Saving product');

                const hasRemovedGroups = !!(form.changes && form.changes.removedGroups && form.changes.removedGroups.length > 0);

                if (hasRemovedGroups && form.active === false) {
                    this.editProduct({ ...form, active: this.productBackup.active }).then((result) => {
                        if (!result.ok) {
                            Vue.toasted.error('An error occurred', { duration: 2000 });
                            return;
                        }
                        this.updateItem({
                            object: 'product',
                            action: 'edit',
                            table: 'products',
                            fields: { supplierId: this.account.supplierId, product: form }
                        }).then(() => {
                            this.closeProduct();
                        }).catch(() => {});
                    });
                    return;
                }

                this.updateItem({
                    object: 'product',
                    action: 'edit',
                    table: 'products',
                    fields: { supplierId: this.account.supplierId, product: form }
                }).then((response) => {
                    if (response && response.status == 200) {
                        this.closeProduct();
                    }
                }).catch((response) => {});
            },
            closeProduct() {
                // Clear both selectedProduct and clonedProduct
                this.clearSelectedItem();
                this.flushEvents();
                this.$router.push({ name: 'inventory' });
            }
        },
        components: {
            ProductLoading,
            ProductEdit,
            ProductSchedule,
            ProductExtras,
            ProductChannels,
            ProductPickups,
            Navigator,
            Steps,
        }
    }
</script>

<style lang="scss">
    .new-product {
        height: auto;
        overflow-x: hidden;
        overflow-y: auto;
    }
</style>



// WEBPACK FOOTER //
// ProductForm.vue?44c6ed5a